import moment from 'moment';
import Swal from 'sweetalert2';
import HelperMixin from '../../../Shared/HelperMixin.vue';

const DEFAULT_FILTERS = () => ({
    categoryId: '',
    manufacturerId: '',
    countryId: '',
    stockingId: '',
    period: 6,
    from: '',
    to: '',
    analysisType: 'quantity',
    includeInactive: false,
    ignoreExpiredBatches: false
});

const DEFAULT_ADVANCED = () => ({
    showPercentages: false,
    autoRefresh: true
});

const TEMPLATE_STORAGE_KEY = 'products-analytics-template-v1';
const AUTO_REFRESH_MS = 5 * 60 * 1000;

const PALETTE = ['#7367F0', '#28C76F', '#FF9F43', '#00CFE8', '#EA5455', '#9F77FF', '#FFB066', '#5DD0BC', '#F08C9D', '#82B1FF', '#A2C70E'];

export default {
    name: 'ProductsAnalytics',
    mixins: [HelperMixin],

    async created() {
        await this.CheckLoginStatus();

        if (!this.loginDetails || !this.hasInventoryAndStockAccessUi()) {
            this.$helper.ShowMessage('error', 'غير مصرح', 'هذه اللوحة متاحة لمستخدمي الوزارة فقط.');
            this.$router.replace('/Home');
            return;
        }

        this.charts = {};
        await this.loadFilterLookups();
        this.applyFilters();
        this.setupAutoRefresh();
    },

    beforeDestroy() {
        this.destroyCharts();
        this.teardownAutoRefresh();
    },

    data() {
        return {
            loading: false,
            lastUpdated: '',
            filters: DEFAULT_FILTERS(),
            advancedOptions: DEFAULT_ADVANCED(),
            lookups: {
                categories: [],
                manufacturers: [],
                countries: [],
                stockings: [],
                periods: [
                    { id: 1, name: 'آخر شهر' },
                    { id: 3, name: 'آخر 3 شهور' },
                    { id: 6, name: 'آخر 6 شهور' },
                    { id: 12, name: 'آخر سنة' },
                    { id: 0, name: 'مخصص' }
                ],
                analysisTypes: [
                    { id: 'quantity', name: 'الكمية' },
                    { id: 'value', name: 'القيمة' }
                ]
            },
            kpi: {
                totalProducts: { value: 0, change: 0 },
                totalQuantity: { value: 0, change: 0 },
                totalValue: { value: 0, change: 0 },
                averagePrice: { value: 0, change: 0 },
                alerts: { value: 0, change: 0 }
            },
            chartData: {
                byCategory: [],
                byStocking: [],
                expiryStatus: { ok: 0, nearExpiry: 0, expired: 0 },
                nearExpiryTop: [],
                topByQuantity: [],
                topByValue: [],
                monthlyTrend: [],
                topRequesters: [],
                byManufacturer: [],
                byCountry: []
            },
            quickInsights: { topCategory: null, topBranch: null, topManufacturer: null, topCountry: null },
            urgentProducts: [],
            topProductsMode: 'quantity',
            trendMode: 'quantity',
            charts: null,
            autoRefreshTimer: null,
            hasSavedTemplate: !!localStorage.getItem(TEMPLATE_STORAGE_KEY)
        };
    },

    computed: {
        activeFiltersCount() {
            const f = this.filters;
            let n = 0;
            if (f.categoryId) n++;
            if (f.manufacturerId) n++;
            if (f.countryId) n++;
            if (f.stockingId) n++;
            if (Number(f.period) !== 6) n++;
            if (f.includeInactive) n++;
            if (f.ignoreExpiredBatches) n++;
            return n;
        },

        kpiCardList() {
            return [
                {
                    key: 'products',
                    title: 'إجمالي المنتجات',
                    icon: 'ti ti-package',
                    tone: 'primary',
                    displayValue: this.formatNumber(this.kpi.totalProducts.value),
                    change: this.kpi.totalProducts.change
                },
                {
                    key: 'qty',
                    title: 'إجمالي الكمية في المخازن',
                    icon: 'ti ti-box',
                    tone: 'success',
                    displayValue: this.formatNumber(this.kpi.totalQuantity.value),
                    change: this.kpi.totalQuantity.change
                },
                {
                    key: 'value',
                    title: 'إجمالي القيمة المخزونة',
                    icon: 'ti ti-coin',
                    tone: 'info',
                    displayValue: `${this.formatCurrency(this.kpi.totalValue.value)} د.ل`,
                    change: this.kpi.totalValue.change
                },
                {
                    key: 'avg',
                    title: 'متوسط سعر الوحدة',
                    icon: 'ti ti-chart-line',
                    tone: 'warning',
                    displayValue: `${this.formatCurrency(this.kpi.averagePrice.value)} د.ل`,
                    change: this.kpi.averagePrice.change
                },
                {
                    key: 'alerts',
                    title: 'تنبيهات المخزون',
                    icon: 'ti ti-alert-triangle',
                    tone: 'danger',
                    displayValue: this.formatNumber(this.kpi.alerts.value),
                    change: this.kpi.alerts.change
                }
            ];
        },

        quickInsightCards() {
            const qi = this.quickInsights || {};
            return [
                {
                    key: 'topCategory',
                    title: 'أعلى تصنيف طلباً',
                    icon: 'ti ti-tag',
                    tone: 'primary',
                    name: qi.topCategory && qi.topCategory.name,
                    percent: qi.topCategory && qi.topCategory.percent,
                    change: qi.topCategory && qi.topCategory.change
                },
                {
                    key: 'topBranch',
                    title: 'أعلى جهة طلباً',
                    icon: 'ti ti-building-hospital',
                    tone: 'success',
                    name: qi.topBranch && qi.topBranch.name,
                    percent: qi.topBranch && qi.topBranch.percent,
                    change: qi.topBranch && qi.topBranch.change
                },
                {
                    key: 'topManufacturer',
                    title: 'أعلى شركة توريداً',
                    icon: 'ti ti-building-factory',
                    tone: 'info',
                    name: qi.topManufacturer && qi.topManufacturer.name,
                    percent: qi.topManufacturer && qi.topManufacturer.percent,
                    change: qi.topManufacturer && qi.topManufacturer.change
                },
                {
                    key: 'topCountry',
                    title: 'أعلى بلد منشأ',
                    icon: 'ti ti-flag',
                    tone: 'warning',
                    name: qi.topCountry && qi.topCountry.name,
                    percent: qi.topCountry && qi.topCountry.percent,
                    change: qi.topCountry && qi.topCountry.change
                }
            ];
        }
    },

    watch: {
        'advancedOptions.autoRefresh'() {
            this.setupAutoRefresh();
        }
    },

    methods: {
        formatNumber(value) {
            const n = Number(value || 0);
            if (!isFinite(n)) return '0';
            return n.toLocaleString('en-US');
        },

        formatCurrency(value) {
            const n = Number(value || 0);
            if (!isFinite(n)) return '0';
            return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
        },

        formatPercent(value) {
            const n = Number(value || 0);
            if (!isFinite(n)) return '0%';
            return `${n.toFixed(1)}%`;
        },

        formatChange(value) {
            const n = Number(value || 0);
            if (!isFinite(n) || n === 0) return '—';
            return `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
        },

        formatDate(value) {
            if (!value) return '—';
            const m = moment(value);
            return m.isValid() ? m.format('DD/MM/YYYY') : '—';
        },

        daysLabel(days) {
            if (days == null || isNaN(days)) return '';
            if (days < 0) return `منتهية منذ ${Math.abs(days)} يوم`;
            if (days === 0) return 'تنتهي اليوم';
            return `${days} يوم متبقي`;
        },

        changeTone(change) {
            const n = Number(change || 0);
            if (n > 0) return 'up';
            if (n < 0) return 'down';
            return 'flat';
        },

        changeIcon(change) {
            const n = Number(change || 0);
            if (n > 0) return 'ti ti-arrow-up-right me-1';
            if (n < 0) return 'ti ti-arrow-down-right me-1';
            return 'ti ti-minus me-1';
        },

        statusBadge(status) {
            switch (status) {
                case 'منتهية': return 'bg-label-danger';
                case 'تنتهي قريباً': return 'bg-label-warning';
                case 'نفذت': return 'bg-label-dark';
                case 'مخزون منخفض': return 'bg-label-info';
                default: return 'bg-label-success';
            }
        },

        quantityBadge(item) {
            if (!item || item.quantity <= 0) return 'bg-label-dark';
            if (item.reorderLevel && item.quantity <= item.reorderLevel) return 'bg-label-warning';
            return 'bg-label-secondary';
        },

        async loadFilterLookups() {
            try {
                const resp = await this.$http.GetProductsAnalyticsFilters();
                const data = resp && resp.data ? resp.data : {};
                this.lookups.categories = data.categories || [];
                this.lookups.manufacturers = data.manufacturers || [];
                this.lookups.countries = data.countries || [];
                this.lookups.stockings = data.stockings || [];
                if (Array.isArray(data.periods) && data.periods.length) this.lookups.periods = data.periods;
                if (Array.isArray(data.analysisTypes) && data.analysisTypes.length) this.lookups.analysisTypes = data.analysisTypes;
            } catch (err) {
                this.lookups.categories = [];
                this.lookups.manufacturers = [];
                this.lookups.countries = [];
                this.lookups.stockings = [];
            }
        },

        normalizedFilters() {
            const f = { ...this.filters };
            if (f.categoryId === '') f.categoryId = null;
            if (f.manufacturerId === '') f.manufacturerId = null;
            if (f.countryId === '') f.countryId = null;
            if (f.stockingId === '') f.stockingId = null;
            return f;
        },

        applyFilters() {
            this.loadAll();
        },

        resetFilters() {
            this.filters = DEFAULT_FILTERS();
            this.applyFilters();
        },

        async loadAll() {
            if (this.loading) return;
            this.loading = true;
            this.$blockUI.Start();

            try {
                const filters = this.normalizedFilters();
                const requests = [
                    this.$http.GetProductsAnalyticsKpi(filters),
                    this.$http.GetProductsAnalyticsChartData(filters),
                    this.$http.GetProductsAnalyticsQuickInsights(filters),
                    this.$http.GetProductsAnalyticsUrgentProducts(filters)
                ];
                const [kpiResp, chartResp, insightsResp, urgentResp] = await Promise.all(requests);

                const kpiData = (kpiResp && kpiResp.data && kpiResp.data.cards) || {};
                this.kpi = {
                    totalProducts: kpiData.totalProducts || { value: 0, change: 0 },
                    totalQuantity: kpiData.totalQuantity || { value: 0, change: 0 },
                    totalValue: kpiData.totalValue || { value: 0, change: 0 },
                    averagePrice: kpiData.averagePrice || { value: 0, change: 0 },
                    alerts: kpiData.alerts || { value: 0, change: 0 }
                };

                const cd = (chartResp && chartResp.data) || {};
                this.chartData = {
                    byCategory: cd.byCategory || [],
                    byStocking: cd.byStocking || [],
                    expiryStatus: cd.expiryStatus || { ok: 0, nearExpiry: 0, expired: 0 },
                    nearExpiryTop: cd.nearExpiryTop || [],
                    topByQuantity: cd.topByQuantity || [],
                    topByValue: cd.topByValue || [],
                    monthlyTrend: cd.monthlyTrend || [],
                    topRequesters: cd.topRequesters || [],
                    byManufacturer: cd.byManufacturer || [],
                    byCountry: cd.byCountry || []
                };

                this.quickInsights = (insightsResp && insightsResp.data) || {};
                this.urgentProducts = (urgentResp && urgentResp.data && urgentResp.data.info) || [];

                this.lastUpdated = moment().format('DD/MM/YYYY HH:mm');

                this.$nextTick(() => this.renderAllCharts());
            } catch (err) {
                this.handleError('تعذر تحميل البيانات', err);
            } finally {
                this.loading = false;
                this.$blockUI.Stop();
            }
        },

        handleError(title, err) {
            const msg = err && err.response && err.response.data ? err.response.data : 'حدث خطأ غير متوقع';
            this.$helper.ShowMessage('error', title, msg);
        },

        setTopProductsMode(mode) {
            this.topProductsMode = mode;
            this.$nextTick(() => this.renderTopProductsChart());
        },

        setTrendMode(mode) {
            this.trendMode = mode;
            this.$nextTick(() => this.renderTrendChart());
        },

        destroyCharts() {
            if (!this.charts) return;
            Object.keys(this.charts).forEach((k) => {
                try {
                    this.charts[k] && this.charts[k].destroy && this.charts[k].destroy();
                } catch (_) { /* ignore */ }
            });
            this.charts = {};
        },

        renderAllCharts() {
            if (typeof window.ApexCharts === 'undefined') {
                setTimeout(() => this.renderAllCharts(), 800);
                return;
            }
            this.destroyCharts();
            this.renderCategoryChart();
            this.renderStockingChart();
            this.renderExpiryChart();
            this.renderTopProductsChart();
            this.renderTrendChart();
            this.renderRequestersChart();
            this.renderManufacturerChart();
            this.renderCountryChart();
        },

        _truncate(str, max = 20) {
            if (!str) return '';
            const s = String(str);
            return s.length > max ? `${s.substring(0, max)}…` : s;
        },

        _percentFormatter() {
            return (val) => `${Math.round(val)}%`;
        },

        _numberFormatter() {
            return (val) => Number(val || 0).toLocaleString('en-US');
        },

        renderCategoryChart() {
            const el = this.$refs.chartCategory;
            if (!el) return;
            const rows = this.chartData.byCategory || [];
            const labels = rows.map((r) => r.name || '—');
            const values = rows.map((r) => r.count || 0);

            const options = {
                chart: { type: 'pie', height: 320, events: this._categoryEvents() },
                series: values.length ? values : [0],
                labels: labels.length ? labels : ['لا توجد بيانات'],
                colors: PALETTE,
                legend: { position: 'bottom' },
                dataLabels: { enabled: true, formatter: this.advancedOptions.showPercentages ? this._percentFormatter() : (v, opts) => `${Math.round(v)}%` },
                tooltip: { y: { formatter: (v) => `${this.formatNumber(v)} منتج` } },
                noData: { text: 'لا توجد بيانات' }
            };

            this.charts.category = new window.ApexCharts(el, options);
            this.charts.category.render();
        },

        _categoryEvents() {
            const self = this;
            return {
                dataPointSelection(_event, _ctx, config) {
                    const idx = config && typeof config.dataPointIndex === 'number' ? config.dataPointIndex : -1;
                    const row = self.chartData.byCategory[idx];
                    if (row) self.showCategoryDrillDown(row);
                }
            };
        },

        async showCategoryDrillDown(row) {
            try {
                const resp = await this.$http.GetProducts(1, 100, '');
                const products = (resp && resp.data && resp.data.info) || [];
                const filtered = products.filter((p) => Number(p.categoriesId) === Number(row.categoryId));
                const html = filtered.length
                    ? `<div class="text-start"><table class="table table-sm">
                        <thead><tr><th>المنتج</th><th>الشركة</th><th>الكود الوطني</th></tr></thead>
                        <tbody>${filtered.slice(0, 30).map((p) => `<tr><td>${p.name || ''}</td><td>${p.model || '-'}</td><td>${p.nationalCode || '-'}</td></tr>`).join('')}</tbody>
                       </table>${filtered.length > 30 ? `<p class="text-muted small mb-0">عرض أول 30 من ${filtered.length}</p>` : ''}</div>`
                    : '<p class="text-muted">لا توجد منتجات في هذا التصنيف ضمن الفلاتر الحالية.</p>';

                Swal.fire({
                    title: `منتجات تصنيف: ${row.name}`,
                    html,
                    icon: 'info',
                    width: 700,
                    confirmButtonText: 'إغلاق'
                });
            } catch (err) {
                this.handleError('تعذر تحميل المنتجات', err);
            }
        },

        renderStockingChart() {
            const el = this.$refs.chartStocking;
            if (!el) return;
            const rows = this.chartData.byStocking || [];
            const labels = rows.map((r) => this._truncate(r.name, 30));
            const useValue = this.filters.analysisType === 'value';
            const values = rows.map((r) => useValue ? Number(r.value || 0) : Number(r.quantity || 0));

            const options = {
                chart: {
                    type: 'bar',
                    height: 320,
                    toolbar: { show: false },
                    events: {
                        dataPointSelection: (_e, _c, config) => {
                            const r = rows[config.dataPointIndex];
                            if (r) this.showStockingDrillDown(r);
                        }
                    }
                },
                series: [{ name: useValue ? 'القيمة' : 'الكمية', data: values }],
                xaxis: { categories: labels },
                colors: ['#28C76F'],
                plotOptions: { bar: { horizontal: true, borderRadius: 4, distributed: false } },
                dataLabels: { enabled: false },
                tooltip: { y: { formatter: this._numberFormatter() } },
                noData: { text: 'لا توجد بيانات' }
            };
            this.charts.stocking = new window.ApexCharts(el, options);
            this.charts.stocking.render();
        },

        showStockingDrillDown(row) {
            Swal.fire({
                title: `تفاصيل المخزن: ${row.name}`,
                html: `<div class="text-start">
                    <p>إجمالي الكمية: <b>${this.formatNumber(row.quantity)}</b></p>
                    <p>إجمالي القيمة: <b>${this.formatCurrency(row.value)} د.ل</b></p>
                </div>`,
                icon: 'info',
                confirmButtonText: 'إغلاق'
            });
        },

        renderExpiryChart() {
            const el = this.$refs.chartExpiry;
            if (!el) return;
            const e = this.chartData.expiryStatus || {};
            const series = [{
                name: 'الدفعات',
                data: [e.ok || 0, e.nearExpiry || 0, e.expired || 0]
            }];

            const options = {
                chart: { type: 'bar', height: 280, stacked: true, toolbar: { show: false } },
                series,
                xaxis: { categories: ['سليمة', 'تنتهي قريباً', 'منتهية'] },
                colors: ['#28C76F', '#FF9F43', '#EA5455'],
                plotOptions: { bar: { horizontal: false, borderRadius: 6, distributed: true } },
                dataLabels: { enabled: true, formatter: this._numberFormatter() },
                legend: { show: false },
                tooltip: { y: { formatter: this._numberFormatter() } },
                noData: { text: 'لا توجد بيانات' }
            };
            this.charts.expiry = new window.ApexCharts(el, options);
            this.charts.expiry.render();
        },

        renderTopProductsChart() {
            const el = this.$refs.chartTopProducts;
            if (!el) return;
            if (this.charts.topProducts) {
                try { this.charts.topProducts.destroy(); } catch (_) { /* ignore */ }
                this.charts.topProducts = null;
            }
            const useValue = this.topProductsMode === 'value';
            const rows = useValue ? (this.chartData.topByValue || []) : (this.chartData.topByQuantity || []);
            const labels = rows.map((r) => this._truncate(r.name, 24));
            const values = rows.map((r) => useValue ? Number(r.value || 0) : Number(r.quantity || 0));

            const options = {
                chart: { type: 'bar', height: 320, toolbar: { show: false } },
                series: [{ name: useValue ? 'القيمة' : 'الكمية', data: values }],
                xaxis: { categories: labels },
                colors: [useValue ? '#7367F0' : '#00CFE8'],
                plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
                dataLabels: { enabled: false },
                tooltip: { y: { formatter: this._numberFormatter() } },
                noData: { text: 'لا توجد بيانات' }
            };
            this.charts.topProducts = new window.ApexCharts(el, options);
            this.charts.topProducts.render();
        },

        renderTrendChart() {
            const el = this.$refs.chartTrend;
            if (!el) return;
            if (this.charts.trend) {
                try { this.charts.trend.destroy(); } catch (_) { /* ignore */ }
                this.charts.trend = null;
            }
            const rows = this.chartData.monthlyTrend || [];
            const labels = rows.map((r) => r.month || '');
            const mode = this.trendMode;
            const values = rows.map((r) => Number(r[mode] || 0));
            const colorByMode = { quantity: '#7367F0', value: '#28C76F', orders: '#FF9F43' };

            const options = {
                chart: { type: 'area', height: 320, toolbar: { show: false } },
                series: [{
                    name: mode === 'quantity' ? 'الكمية المصروفة' : (mode === 'value' ? 'القيمة المصروفة' : 'عدد الطلبات'),
                    data: values
                }],
                xaxis: { categories: labels },
                colors: [colorByMode[mode] || '#7367F0'],
                stroke: { curve: 'smooth', width: 2 },
                fill: { type: 'gradient', gradient: { opacityFrom: 0.4, opacityTo: 0.05 } },
                dataLabels: { enabled: false },
                tooltip: { y: { formatter: this._numberFormatter() } },
                noData: { text: 'لا توجد بيانات' }
            };
            this.charts.trend = new window.ApexCharts(el, options);
            this.charts.trend.render();
        },

        renderRequestersChart() {
            const el = this.$refs.chartRequesters;
            if (!el) return;
            const rows = this.chartData.topRequesters || [];
            const labels = rows.map((r) => this._truncate(r.name, 18));
            const values = rows.map((r) => Number(r.quantity || 0));

            const options = {
                chart: {
                    type: 'bar',
                    height: 320,
                    toolbar: { show: false },
                    events: {
                        dataPointSelection: (_e, _c, config) => {
                            const r = rows[config.dataPointIndex];
                            if (r) this.showRequesterDrillDown(r);
                        }
                    }
                },
                series: [{ name: 'الكمية المطلوبة', data: values }],
                xaxis: { categories: labels, labels: { rotate: -30, hideOverlappingLabels: false } },
                colors: ['#9F77FF'],
                plotOptions: { bar: { horizontal: false, borderRadius: 4, columnWidth: '60%' } },
                dataLabels: { enabled: false },
                tooltip: { y: { formatter: this._numberFormatter() } },
                noData: { text: 'لا توجد بيانات' }
            };
            this.charts.requesters = new window.ApexCharts(el, options);
            this.charts.requesters.render();
        },

        showRequesterDrillDown(row) {
            Swal.fire({
                title: `طلبات الجهة: ${row.name}`,
                html: `<div class="text-start">
                    <p>إجمالي الكمية المطلوبة: <b>${this.formatNumber(row.quantity)}</b></p>
                    <p>عدد الطلبات: <b>${this.formatNumber(row.ordersCount)}</b></p>
                </div>`,
                icon: 'info',
                confirmButtonText: 'إغلاق'
            });
        },

        renderManufacturerChart() {
            const el = this.$refs.chartManufacturer;
            if (!el) return;
            const rows = (this.chartData.byManufacturer || []).slice(0, 10);
            const labels = rows.map((r) => this._truncate(r.name, 20));
            const values = rows.map((r) => Number(r.quantity || 0));

            const options = {
                chart: { type: 'donut', height: 320 },
                series: values.length ? values : [0],
                labels: labels.length ? labels : ['لا توجد بيانات'],
                colors: PALETTE,
                legend: { position: 'bottom' },
                dataLabels: { enabled: this.advancedOptions.showPercentages, formatter: this._percentFormatter() },
                plotOptions: { pie: { donut: { size: '60%' } } },
                tooltip: { y: { formatter: this._numberFormatter() } },
                noData: { text: 'لا توجد بيانات' }
            };
            this.charts.manufacturer = new window.ApexCharts(el, options);
            this.charts.manufacturer.render();
        },

        renderCountryChart() {
            const el = this.$refs.chartCountry;
            if (!el) return;
            const rows = (this.chartData.byCountry || []).slice(0, 10);
            const labels = rows.map((r) => this._truncate(r.name, 20));
            const values = rows.map((r) => Number(r.quantity || 0));

            const options = {
                chart: { type: 'pie', height: 320 },
                series: values.length ? values : [0],
                labels: labels.length ? labels : ['لا توجد بيانات'],
                colors: PALETTE,
                legend: { position: 'bottom' },
                dataLabels: { enabled: this.advancedOptions.showPercentages, formatter: this._percentFormatter() },
                tooltip: { y: { formatter: this._numberFormatter() } },
                noData: { text: 'لا توجد بيانات' }
            };
            this.charts.country = new window.ApexCharts(el, options);
            this.charts.country.render();
        },

        // Auto-refresh
        setupAutoRefresh() {
            this.teardownAutoRefresh();
            if (this.advancedOptions.autoRefresh) {
                this.autoRefreshTimer = setInterval(() => {
                    if (!this.loading) this.loadAll();
                }, AUTO_REFRESH_MS);
            }
        },

        teardownAutoRefresh() {
            if (this.autoRefreshTimer) {
                clearInterval(this.autoRefreshTimer);
                this.autoRefreshTimer = null;
            }
        },

        // Templates
        saveTemplate() {
            try {
                const payload = JSON.stringify({ filters: this.filters, advancedOptions: this.advancedOptions });
                localStorage.setItem(TEMPLATE_STORAGE_KEY, payload);
                this.hasSavedTemplate = true;
                this.$helper.ShowMessage('success', 'تم الحفظ', 'تم حفظ إعدادات الفلاتر كقالب محلي.');
            } catch (_) {
                this.$helper.ShowMessage('error', 'خطأ', 'تعذّر حفظ القالب.');
            }
        },

        loadTemplate() {
            try {
                const raw = localStorage.getItem(TEMPLATE_STORAGE_KEY);
                if (!raw) return;
                const parsed = JSON.parse(raw);
                if (parsed && parsed.filters) this.filters = { ...DEFAULT_FILTERS(), ...parsed.filters };
                if (parsed && parsed.advancedOptions) this.advancedOptions = { ...DEFAULT_ADVANCED(), ...parsed.advancedOptions };
                this.applyFilters();
            } catch (_) {
                this.$helper.ShowMessage('error', 'خطأ', 'تعذّر قراءة القالب المحفوظ.');
            }
        },

        scheduleEmailReport() {
            Swal.fire({
                title: 'إرسال دوري بالإيميل',
                html: `<div class="text-start">
                    <p>سيتم تجهيز هذه الميزة لاحقاً مع وحدة الإشعارات الدورية.</p>
                    <p class="text-muted small">يمكن حالياً استخدام زر "تصدير PDF" لطباعة لقطة فورية من اللوحة.</p>
                </div>`,
                icon: 'info',
                confirmButtonText: 'حسناً'
            });
        },

        async exportPdf() {
            try {
                await this.$http.ExportProductsAnalyticsPdf(this.normalizedFilters());
            } catch (_) { /* non-blocking */ }
            this.$nextTick(() => window.print());
        },

        // Urgent product actions
        openUrgentDispense(row) {
            this.$router.push({ path: '/StockVouchers', query: { productId: String(row.productId || ''), batch: row.serialNumber || '' } });
        },

        openSupplyRequest(row) {
            this.$router.push({ path: '/Procurement', query: { productId: String(row.productId || '') } });
        },

        openStockAdjustment(row) {
            this.$router.push({ path: '/InventoryCounts', query: { productId: String(row.productId || ''), stockingId: String(row.stockingId || '') } });
        }
    }
};
